import React, { useState, useEffect } from "react";
import Grid from "utils/Grid";
import Box from "utils/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import TablePagination from "@mui/material/TablePagination";
import GetAppIcon from "@mui/icons-material/GetApp";
import TablePaginationActions from "components/Common/TableGenerator/TablePaginationActions";
import moment from "dayjs";
import _ from "lodash";
import { connect, useSelector } from "react-redux";
import { domainLevelConfig } from "ui-config";
import { useSnackbar } from "notistack";
import { downloadBlob } from "helpers";
import { ButtonIcon, ApplyButton } from "components/Common/Buttons";
import CustomInputDatePicker from "../../../../../Common/CustomInputDatePicker";
import AsyncAutoComplete from "../common/AsyncAutoComplete";
import {
  convertUtcToLocal,
  getFilterStartDate,
  getFilterEndDate,
  getTimeZoneDifference,
  dateFormatter,
} from "utils/helpers";
import styles from "./styles";

function Transactions(props) {
  const classes = styles();

  const operatorsData = useSelector(
    (state) => state.dropdownReducer.operatorsData
  );
  const areasData = useSelector((state) => state.dropdownReducer.areasData);
  const locationsData = useSelector(
    (state) => state.dropdownReducer.locationsData
  );
  const vmData = useSelector((state) => state.dropdownReducer.devicesData);
  const productsData = useSelector(
    (state) => state.dropdownReducer.productsData
  );
  const host = useSelector((state) => state.userReducer.host);

  const [startDate, setStartDate] = useState(
    moment().subtract(15, "days").toDate()
  );
  const [endDate, setEndDate] = useState(new Date());
  const [locationList, setLocationList] = useState(locationsData || []);
  const [areaList, setAreaList] = useState(areasData || []);
  const [vmList, setVmList] = useState(vmData || []);
  const [fetchDataParams, setFetchDataParams] = useState({});
  const [filters, setFilters] = useState({
    operator: { value: "all", label: "All" },
    area: { value: "all", label: "All" },
    location: { value: "all", label: "All" },
    vm: { value: "all", label: "All" },
    product: { value: "all", label: "All" },
  });
  const [transaction, setTransaction] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [loader, setLoader] = useState(false);
  const [exportingPDFLoader, setExportingPDFLoader] = useState(false);
  const [exportingExcelLoader, setExportingExcelLoader] = useState(false);
  const [areaListLoading, setAreaListLoading] = useState(false);
  const [locationListLoading, setLocationListLoading] = useState(false);
  const [vmListLoading, setVmListLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const updateFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const fetchLocations = async (areaList = [], area_id, selectedOperator) => {
    try {
      setLocationListLoading(true);

      if (area_id) {
        const list =
          _.find(areaList, (x) => x.value === area_id)?.locations || [];

        setLocationList([
          { label: "All", value: "all" },
          ..._.map(list, ({ location_id, location_name }) => ({
            label: location_name,
            value: location_id,
          })),
        ]);
      } else {
        if (selectedOperator && selectedOperator?.value !== "all") {
          let newLocation = [];
          const locationData = [
            ..._.map(areaList, ({ locations }) =>
              Array.prototype.push.apply(newLocation, locations)
            ),
          ];
          setLocationList([
            { label: "All", value: "all" },
            ..._.map(newLocation, ({ location_id, location_name }) => ({
              label: location_name,
              value: location_id,
            })),
          ]);
        } else {
          const { data } = await window.axiosIns("locations/locations", {
            params: {
              all: true,
              state: "all",
              ordering: "location_name",
              lite: true,
            },
          });
          setLocationList([
            { label: "All", value: "all" },
            ..._.map(data?.results, ({ location_id, location_name }) => ({
              label: location_name,
              value: location_id,
            })),
          ]);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLocationListLoading(false);
    }
  };

  const fetchVM = async (selectedOperator, selectedArea, selectedLocation) => {
    try {
      setVmListLoading(true);
      const params = {
        all: true,
        ordering: "vm_name",
        lite: true,
      };
      if (selectedOperator && selectedOperator.value !== "all")
        params.company_id = selectedOperator.value;
      if (selectedArea && selectedArea.value !== "all")
        params.area_id = selectedArea.value;
      if (selectedLocation && selectedLocation.value !== "all")
        params.location = selectedLocation.value;
      const { data } = await window.axiosIns("device", {
        params,
      });
      setVmList([
        { label: "All", value: "all" },
        ..._.map(data?.data?.results, ({ id, serial_number, vm_name }) => ({
          label: `${vm_name ? `${vm_name} ||` : ""} ${serial_number}`,
          value: id,
        })),
      ]);
    } catch (err) {
      console.log(err);
      setVmListLoading(false);
    } finally {
      setVmListLoading(false);
    }
  };

  const fetchAreas = async (selectedOperator) => {
    try {
      setAreaListLoading(true);

      const params = { all: true, ordering: "area_name" };
      if (selectedOperator && selectedOperator.value !== "all")
        params.operator_id = selectedOperator.value;

      const { data } = await window.axiosIns("locations/areas", {
        params,
      });

      setAreaList([
        { label: "All", value: "all" },
        ..._.map(data?.results, ({ area_id, area_name, locations }) => ({
          label: area_name,
          value: area_id,
          locations,
        })),
      ]);
      fetchLocations(areaList, null, selectedOperator);
    } catch (err) {
      console.log(err);
      fetchLocations([], null, selectedOperator);
    } finally {
      setAreaListLoading(false);
    }
  };

  const getCommonParams = () => {
    const params = {
      start_date: getFilterStartDate(startDate),
      end_date: getFilterEndDate(endDate),
      tz: getTimeZoneDifference?.(),
      _scope: "AND",
    };

    const optionalFilters = {
      device_id: filters.vm,
      device__location_id: filters.location,
      device__location__area_id: filters.area,
      device__company_id: filters.operator,
      order__products: filters.product,
    };

    Object.entries(optionalFilters).forEach(([key, val]) => {
      if (val?.value && val.value !== "all") {
        params[key] = val.value;
      }
    });

    return params;
  };

  const fetchExcel = async () => {
    if (count > 35000) {
      return enqueueSnackbar(
        "Number of Records can not be more than 35000 for Excel download. Please lower the date range.",
        { autoHideDuration: 4000 }
      );
    }

    setExportingExcelLoader(true);
    try {
      const params = getCommonParams();
      const { data } = await window.axiosIns("transactions/export_data", {
        responseType: "blob",
        params,
      });

      if (data) {
        downloadBlob(
          new Blob([data], { type: "application/xlsx" }),
          "transactions_report.xlsx"
        );
      }
    } catch (err) {
      console.log(err);
    } finally {
      setExportingExcelLoader(false);
    }
  };

  const fetchPDF = async () => {
    if (count > 15000) {
      return enqueueSnackbar(
        "Number of Records can not be more than 15000 for PDF download. Please lower the date range.",
        { autoHideDuration: 4000 }
      );
    }

    setExportingPDFLoader(true);
    try {
      const params = {
        ...getCommonParams(),
        type: "pdf",
        model: "transactions",
      };
      const { data } = await window.axiosIns("transactions/export", {
        responseType: "blob",
        params,
      });

      if (data) {
        downloadBlob(new Blob([data]), "transaction_reports.pdf");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setExportingPDFLoader(false);
    }
  };

  const fetchTransaction = async (_page, _limit, _runreport) => {
    if (_runreport) setPage(0);

    const params = {
      ...getCommonParams(),
      page: _runreport ? 1 : _page + 1,
      limit: _limit,
    };

    if (_.isEqual(params, fetchDataParams)) return;

    setFetchDataParams(params);
    setLoader(true);

    try {
      const { data } = await window.axiosIns("transactions", { params });
      setCount(data.count);
      setTransaction(data.results);
    } catch (err) {
      console.log(err);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchTransaction(0, 10);
  }, []);

  const handleChangePage = (_, page) => {
    setPage(page);
    fetchTransaction(page, limit);
  };

  const handleChangeRowsPerPage = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
    fetchTransaction(0, parseInt(event.target.value, 10));
  };

  const hostConfig =
    domainLevelConfig[host] || domainLevelConfig["default"] || {};

  const handleOperator = (val) => {
    updateFilter("operator", val);
    fetchAreas(val);
    fetchVM(val);
    updateFilter("area", { value: "all", label: "All" });
    updateFilter("location", { value: "all", label: "All" });
    updateFilter("vm", { value: "all", label: "All" });
    setPage(0);
  };

  const handleArea = (val) => {
    updateFilter("area", val);
    fetchLocations(areaList, val.value, filters.operator);
    fetchVM(filters.operator, val);
    updateFilter("location", { value: "all", label: "All" });
    updateFilter("vm", { value: "all", label: "All" });
    setPage(0);
  };

  const handleLocation = (val) => {
    updateFilter("location", val);
    fetchVM(filters.operator, filters.area, val);
    updateFilter("vm", { value: "all", label: "All" });
    setPage(0);
  };
  return (
    <div id="sa-modules-wrapper" className={classes.wrapper}>
      <Grid spacing={1} container style={{ marginBottom: "1rem" }}>
        <Grid xs={10}>
          <Grid container spacing={1}>
            <Grid xs={3}>
              <CustomInputDatePicker
                value={startDate}
                inputClassName={classes.inputDatePicker}
                onChange={(date) => {
                  setStartDate(date);
                }}
                className="mr-2"
                label="Start Date"
                maxDate={new Date(endDate).setDate(new Date(endDate).getDate())}
              />
            </Grid>
            <Grid xs={3}>
              <CustomInputDatePicker
                value={endDate}
                inputClassName={classes.inputDatePicker}
                onChange={(date) => setEndDate(date)}
                label="End Date"
                minDate={new Date(startDate)}
                maxDate={new Date()}
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={handleOperator}
                value={filters.operator}
                loading={false}
                options={operatorsData || []}
                label="Operator"
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={handleArea}
                loading={areaListLoading}
                value={filters.area}
                options={areaList}
                label="Area"
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={handleLocation}
                value={filters.location}
                loading={locationListLoading}
                options={locationList}
                label="Location"
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={(val) => {
                  updateFilter("vm", val);
                  setPage(0);
                }}
                options={vmList}
                loading={vmListLoading}
                value={filters.vm}
                label="Vending Machine"
              />
            </Grid>
            <Grid xs={3}>
              <AsyncAutoComplete
                onChange={(val) => {
                  updateFilter("product", val);
                  setPage(0);
                }}
                options={productsData || []}
                loading={false}
                value={filters.product}
                label="Products"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid xs={2}>
          <ApplyButton
            size="large"
            fullWidth
            onClick={() => fetchTransaction(page, limit, true)}
            style={{ marginBottom: "16px", padding: "15px 0" }}
            disabled={loader || exportingPDFLoader || exportingExcelLoader}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <ButtonIcon
              label="Excel"
              fullWidth
              size="large"
              Icon={GetAppIcon}
              onClick={fetchExcel}
              disabled={exportingExcelLoader || transaction?.length <= 0}
              style={{ marginRight: "4px", padding: "15px 0" }}
              loading={exportingExcelLoader}
            />
            <ButtonIcon
              label="PDF"
              fullWidth
              size="large"
              Icon={GetAppIcon}
              onClick={fetchPDF}
              disabled={exportingPDFLoader || transaction?.length <= 0}
              style={{ padding: "15px 0" }}
              loading={exportingPDFLoader}
            />
          </div>
        </Grid>
      </Grid>
      <Box
        mt={4}
        className="mt-3 pt-4 px-4"
        style={{
          boxShadow: "0 0 10px lightgrey",
          fontSize: "0.8rem",
          margin: "0",
          maxWidth: "100%",
        }}
      >
        <Grid container>
          <Grid xs={3}>
            <div className={classes.navLogoWrapper}>
              <div
                className={classes.navLogo}
                style={{
                  backgroundImage: `url(${
                    props.appLogo
                      ? props.appLogo
                      : hostConfig.navLogo ||
                        props.currentUser.company.logo ||
                        hostConfig.navLogo
                  })`,
                }}
              ></div>
            </div>
          </Grid>
          <Grid xs={6}>
            <Typography align="center" variant="h3">
              Transaction Report
            </Typography>
            {startDate &&
              endDate &&
              filters.area.value &&
              filters.location.value &&
              filters.operator.value &&
              filters.vm.value && (
                <Typography align="center">
                  {`Date Range:${dateFormatter(startDate)} to ${dateFormatter(
                    endDate
                  )}, Operator: ${filters.operator.label}, Area: ${
                    filters.area.label
                  }, Location: ${filters.location.label}, Vending Machine: ${
                    filters.vm.label
                  }, Product: ${filters.product.label}`}
                </Typography>
              )}
          </Grid>
          <Grid xs={3}>
            <Box justifyContent="flex-end" display="flex">
              <Typography>
                <b>
                  Generated on :{" "}
                  {new Date().toLocaleString("en-us", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </b>
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <div
          style={{ height: "auto", overflow: "auto", overflowX: "hidden" }}
          className="hide-scrollbar"
        >
          <div className="table-responsive-sm mt-4">
            <table className="table logs-table w-100">
              <thead>
                <tr className="border-none">
                  {props.currentUser.type === "SA" &&
                    filters.operator.value === "all" && (
                      <th scope="col" style={{ verticalAlign: "top" }}>
                        Operator
                      </th>
                    )}
                  {props.currentUser.type === "SA" &&
                    filters.location.value === "all" && (
                      <th scope="col" style={{ verticalAlign: "top" }}>
                        Location
                      </th>
                    )}
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    Serial Number
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    VM Name
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    Transaction Id
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    Order Id
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    Status
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    Gateway
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    Amount ($)
                  </th>
                  <th scope="col" style={{ verticalAlign: "top" }}>
                    Time
                  </th>
                </tr>
              </thead>
              {!loader && (
                <tbody>
                  {transaction.map((res) => {
                    return (
                      <tr key={res.timestamp} className="new-entry">
                        {props.currentUser.type === "SA" &&
                          filters.operator.value === "all" && (
                            <td
                              style={{
                                width: "5%",
                                padding: "0.75rem 0.25rem",
                                wordBreak: "break-all",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {res.operator_name ?? ""}
                            </td>
                          )}
                        {props.currentUser.type === "SA" &&
                          filters.location.value === "all" && (
                            <td
                              style={{
                                width: "5%",
                                padding: "0.75rem 0.25rem",
                                wordBreak: "break-all",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {res.location_name ?? ""}
                            </td>
                          )}
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                          }}
                        >
                          {res.device_serial ?? ""}
                        </td>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                          }}
                        >
                          {res.vm_name ?? ""}
                        </td>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {res.transaction_id}
                        </td>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {res.order_id?.split("_")[0] ?? ""}
                        </td>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {res.status}
                        </td>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {res.gateway}
                        </td>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-all",
                          }}
                        >
                          {res.amount}
                        </td>
                        <td
                          style={{
                            width: "5%",
                            padding: "0.75rem 0.25rem",
                            wordBreak: "break-word",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {res.created_at
                            ? dateFormatter(
                                convertUtcToLocal(res.created_at),
                                true
                              )
                            : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
            {transaction.length === 0 && (
              <div style={{ textAlign: "center", marginBottom: "16px" }}>
                <strong> No records found</strong>
              </div>
            )}
            {loader && (
              <Box display="flex" justifyContent="center" mt={1}>
                <CircularProgress style={{ height: 35, width: 35 }} />
              </Box>
            )}
          </div>
        </div>
      </Box>
      <TablePagination
        rowsPerPageOptions={[3, 5, 10, 25, 50, 100]}
        count={count}
        rowsPerPage={limit}
        page={page}
        slotProps={{ select: { "aria-label": "rows per page" } }}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={(e) => TablePaginationActions(e, props)}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    currentUser: state.userReducer.current_user,
    appLogo: state.userReducer.appLogo,
  };
};

export default connect(mapStateToProps)(Transactions);
